// Sessions planned in bulk (docs/cms-hub.md): the days and times a season
// repeats, in São Paulo time whatever the editor's computer says. Brazil
// keeps no daylight saving time since 2019, so its offset is always -03:00.

export const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
// The week from Monday, so a weekend's days sit together.
export const WEEK_FROM_MONDAY = [1, 2, 3, 4, 5, 6, 0]

export type SessionTime = { start: string; end: string | null }
// The times of each chosen weekday, which may differ (PRD.md, item 18).
export type DayTimes = Partial<Record<number, SessionTime[]>>
export type SessionSlot = SessionTime & { weekday: number }
export type PlannedSession = { starts_at: string; ends_at: string | null }
type ExistingSession = { starts_at: string; ends_at?: string | null; status?: string }

const MAX_DAYS = 400

const pad = (value: number) => String(value).padStart(2, '0')

// A day ("2026-10-18") and a time ("21:00") in São Paulo, as an instant.
export const saoPauloInstant = (date: string, time: string) => new Date(`${date}T${time}:00-03:00`).toISOString()

// An instant's day, time and weekday in São Paulo.
export function saoPauloParts(iso: string) {
  const local = new Date(new Date(iso).getTime() - 3 * 3_600_000)
  return {
    date: `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}`,
    time: `${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`,
    weekday: local.getUTCDay(),
  }
}

export function addDays(date: string, days: number) {
  const [year, month, day] = date.split('-').map(Number)
  const next = new Date(Date.UTC(year, month - 1, day + days))
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`
}

export const weekdayOf = (date: string) => {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

export const todayInSaoPaulo = () => saoPauloParts(new Date().toISOString()).date

// A session that ends at or before its start crosses midnight.
export function plannedAt(date: string, time: SessionTime): PlannedSession {
  return {
    starts_at: saoPauloInstant(date, time.start),
    ends_at: time.end ? saoPauloInstant(time.end <= time.start ? addDays(date, 1) : date, time.end) : null,
  }
}

// Every chosen weekday between two days, each with its own times, in order.
export function planSessions({ from, to, days }: { from: string; to: string; days: DayTimes }): PlannedSession[] {
  if (!from || !to || to < from) return []
  const planned: PlannedSession[] = []
  for (let date = from, count = 0; date <= to && count < MAX_DAYS; date = addDays(date, 1), count++) {
    for (const time of days[weekdayOf(date)] ?? []) {
      if (/^\d{2}:\d{2}$/.test(time.start)) planned.push(plannedAt(date, time))
    }
  }
  return planned.sort((left, right) => left.starts_at.localeCompare(right.starts_at))
}

const isLive = (session: ExistingSession) => session.status !== 'deleted' && session.status !== 'canceled'

// How a season repeats: each weekday and time of its live sessions in the
// four weeks up to its last day, or all of them when it is shorter.
export function weeklyPattern(sessions: ExistingSession[]): SessionSlot[] {
  const live = sessions.filter((session) => session.starts_at && isLive(session))
  if (!live.length) return []
  const last = live.map((session) => saoPauloParts(session.starts_at).date).sort().at(-1) as string
  const since = addDays(last, -27)
  const slots = new Map<string, SessionSlot>()
  for (const session of live) {
    const start = saoPauloParts(session.starts_at)
    if (start.date < since) continue
    const end = session.ends_at ? saoPauloParts(session.ends_at).time : null
    slots.set(`${start.weekday}|${start.time}|${end ?? ''}`, { weekday: start.weekday, start: start.time, end })
  }
  return [...slots.values()].sort((left, right) => left.weekday - right.weekday || left.start.localeCompare(right.start))
}

// The pattern's sessions from the day after the season's last until a new last
// day, never before now: a season that ended weeks ago resumes from today, not
// in the weeks that passed (PRD.md, item 117).
export function extendSessions(sessions: ExistingSession[], until: string, now = new Date()): PlannedSession[] {
  const pattern = weeklyPattern(sessions)
  const live = sessions.filter((session) => session.starts_at && isLive(session))
  if (!pattern.length || !until) return []
  const last = live.map((session) => saoPauloParts(session.starts_at).date).sort().at(-1) as string
  const today = saoPauloParts(now.toISOString()).date
  const planned: PlannedSession[] = []
  for (let date = addDays(last < today ? today : last, last < today ? 0 : 1), count = 0; date <= until && count < MAX_DAYS; date = addDays(date, 1), count++) {
    for (const slot of pattern.filter((item) => item.weekday === weekdayOf(date))) planned.push(plannedAt(date, slot))
  }
  return planned.filter((session) => new Date(session.starts_at) > now)
}

// What a plan adds: the sessions not already there at the same instant.
export function withoutExisting(planned: PlannedSession[], existing: ExistingSession[]) {
  const taken = new Set(existing.filter((session) => session.status !== 'deleted').map((session) => new Date(session.starts_at).getTime()))
  return planned.filter((session) => !taken.has(new Date(session.starts_at).getTime()))
}

// "Sex e Sáb às 21:00 · Dom às 18:00": the days that share their times
// together, the week read from Monday, as a weekend's programme is.
const fromMonday = (weekday: number) => (weekday + 6) % 7

export function describePattern(slots: SessionSlot[]) {
  const byTimes = new Map<string, number[]>()
  const weekdays = [...new Set(slots.map((slot) => slot.weekday))].sort((left, right) => fromMonday(left) - fromMonday(right))
  for (const weekday of weekdays) {
    const times = slots.filter((slot) => slot.weekday === weekday)
      .map((slot) => (slot.end ? `${slot.start} – ${slot.end}` : slot.start)).join(', ')
    byTimes.set(times, [...(byTimes.get(times) ?? []), weekday])
  }
  return [...byTimes.entries()].map(([times, weekdays]) => {
    const days = weekdays.map((weekday) => WEEKDAY_LABELS[weekday])
    const joined = days.length > 1 ? `${days.slice(0, -1).join(', ')} e ${days.at(-1)}` : days[0]
    return `${joined} às ${times}`
  }).join(' · ')
}
